/* @flow */
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import {ccHomeCommands} from '../../constants/wamConstants';
import AnalyticsUtil, {getComName, getSvcName} from '../../utils/AnalyticsUtil';
import thorUtil from '../../utils/wam/ThorUtil';

export default class InlineProductModel {
	sapCode: string;
	version: string;
	displayName: ?string;
	entitled: ?boolean;
	fallBackOnFailure: ?Function;
	cmdSuccessCallback: ?Function;
	shouldUpdateCTAOnFailure: boolean;

	constructor(sapCode: string, version: string, displayName?: string, entitled?: boolean) {
		this.sapCode = sapCode;
		this.version = version;
		this.displayName = displayName;
		this.entitled = entitled;
		this.shouldUpdateCTAOnFailure = true;
	}

	setShouldUpdateCTAOnFailure(shouldUpdateCTAOnFailure: boolean) {
		this.shouldUpdateCTAOnFailure = shouldUpdateCTAOnFailure;
	}

	commandToThor(commandPayload: Object) {
		const cmdContext = {
			sapCode: this.sapCode,
			version: this.version,
			commandPayload: {
				command: commandPayload.command
			},
			isConfirmationType: commandPayload.isConfirmation,
			confirmationId: commandPayload.confirmationID,
			confirmationType: commandPayload.confirmationType,
			confirmationBtnVal: commandPayload.confirmationButtonValue,
			confirmationCheckboxes: commandPayload.confirmationCheckboxes,
		};
		if (commandPayload.showDialog) {
			cmdContext.commandPayload.showDialog = 'true';
		}
		const commandContext = {...cmdContext};
		commandContext.ingestContext = {
			contextGuid: AnalyticsUtil.contextGuid,
			contextParams: AnalyticsUtil.contextParams
		};
		const contextComName = getComName();
		const contextSvcName = getSvcName();
		commandContext.ingestContext = {
			...commandContext.ingestContext,
			contextComName,
			contextSvcName
		};


		thorUtil.getWAMClient().sendCommand(commandContext)
			.then((isThorConnectedForInline) => {
				try {
					if (isThorConnectedForInline) {
						this.cmdSuccessCallback && this.cmdSuccessCallback();
						if (commandPayload.command && ['try', 'install', 'update'].indexOf(commandPayload.command) > -1) {
							this.onAppDownloadEvent(commandPayload.command);
						}
					}
				}
				catch (e) {
					console.log(e);
				}
			})
			.catch((returnResult) => {
				if (!returnResult.status) {
					this.fallBackOnFailure && this.fallBackOnFailure();
				}
			});
	}

	sendConfirmation(confirmationID: string, confirmationType: string, confirmationButtonValue: string, confirmationCheckboxes: ?Object) {
		this.commandToThor({isConfirmation: true, confirmationID, confirmationType, confirmationButtonValue , confirmationCheckboxes});
	}

	stopPropagation(e: ?Object) {
		if (e) {
			e.stopPropagation();
		}
	}

	sendAnalytics(eventConfig: Object) {
		const appAnalyticsHandler = thorUtil.getAppsAnalyticsHandler();
		const eventName = eventConfig.eventName;
		const subType = eventConfig.command;
		const eventValue = (eventConfig.searchKeyword && eventConfig.searchKeyword === 'got-it') ? 'auto' : eventConfig.eventValue;
		const searchKeyword = eventConfig.searchKeyword;
		const contextSequence = eventConfig.searchKeyword;
		if (eventValue === 'auto') {
			appAnalyticsHandler.onCCHToThorCommandForInline({eventName, subType, eventValue, searchKeyword, contextSequence}, {
				sapCode: this.sapCode,
				displayName: this.displayName,
				platform: 'desktop',
				entitled: this.entitled,
				version: this.version
			});
		}
	}

	onAppDownloadEvent(cmdName: string) {
		const appAnalyticsHandler = thorUtil.getAppsAnalyticsHandler();
		appAnalyticsHandler.onAppDownloadEvent({
			sapCode: this.sapCode,
			displayName: this.displayName,
			platform: 'desktop',
			entitled: this.entitled,
			version: this.version
		}, 'App Inline', cmdName);
	}

	install(e: ?Object, analyticsCmdname: ?string, searchKeyword: ?string) {
		this.stopPropagation(e);
		const cmdName = analyticsCmdname || 'install';
		this.sendAnalytics({eventName: 'click', command: cmdName, searchKeyword});
		this.commandToThor({command: ccHomeCommands.ccHomeToThorCommands.install});
	}
	autoInstall() {
		this.sendAnalytics({eventName: 'click', command: 'install', eventValue: 'auto'});
		this.commandToThor({command: ccHomeCommands.ccHomeToThorCommands.install});
	}
	autoLaunch() {
		this.sendAnalytics({eventName: 'click', command: 'open', eventValue: 'auto'});
		this.commandToThor({command: ccHomeCommands.ccHomeToThorCommands.launch});
	}
	autoUpdate() {
		this.sendAnalytics({eventName: 'click', command: 'update', eventValue: 'auto'});
		this.commandToThor({command: ccHomeCommands.ccHomeToThorCommands.update});
	}

	update(e: ?Object, searchKeyword: ?string) {
		this.stopPropagation(e);
		this.sendAnalytics({eventName: 'click', command: 'update', searchKeyword});
		this.commandToThor({command: ccHomeCommands.ccHomeToThorCommands.update});
	}

	updateAll(e: ?Object, searchKeyword: ?string) {
		this.stopPropagation(e);
		this.sendAnalytics({eventName: 'click', command: 'updateAll', searchKeyword});
		this.commandToThor({command: ccHomeCommands.ccHomeToThorCommands.updateAll});
	}

	cancel(e: ?Object, progressStatus: ?string) {
		this.stopPropagation(e);
		this.sendAnalytics({eventName: 'click', command: 'cancel', eventValue: progressStatus});
		this.commandToThor({command: ccHomeCommands.ccHomeToThorCommands.cancel});
	}

	retry(e: ?Object) {
		this.stopPropagation(e);
		this.sendAnalytics({eventName: 'click', command: 'retry'});
		this.commandToThor({command: ccHomeCommands.ccHomeToThorCommands.retry});
	}

	uninstall(e: ?Object) {
		this.stopPropagation(e);
		this.sendAnalytics({eventName: 'click', command: 'uninstall'});
		this.commandToThor({command: ccHomeCommands.ccHomeToThorCommands.uninstall});
	}

	startTrial(e: ?Object, searchKeyword: ?string) {
		this.stopPropagation(e);
		this.sendAnalytics({eventName: 'click', command: 'startTrial', searchKeyword});
		this.commandToThor({command: ccHomeCommands.ccHomeToThorCommands.startTrial});
	}

	launch(showDialog: ?boolean, e: ?Object, searchKeyword: ?string) {
		this.stopPropagation(e);
		this.sendAnalytics({eventName: 'click', command: 'open', searchKeyword});
		this.commandToThor({command: ccHomeCommands.ccHomeToThorCommands.launch, showDialog});
	}
}
